import { supertypeClass, property, Remoteable, Supertype } from '@haventech/amorphic';
import { BenefitCode, PaymentFrequency } from '@hit/policy-admin-types';
import {
  BenefitPremiumDTO,
  FlatExtraDTO,
  InvoiceDTO,
  PolicyNoteDTO,
  ProductInfoPayload,
  SegmentPremiumBreakdownDTO,
  SegmentPremiumDTO,
} from '@hit/policy-admin-types/dist/data-model/Types';
import { GirSectionDTO, RtrSectionDTO, SegmentDTO } from '@hit/policy-admin-types/dist/openapi-docs/gen/ApiTypes';
import { Utils } from '../Utils';
import { CACPostRiderDTO } from '../../../coverpath-common/js/interfaces/CoverpathTypes';

export declare type CirSectionDTO = {
  coverageAmount: number;
  underwritingClass: string;
  monthlyAccelerationPercentage: number;
  activeSegment: SegmentDTO;
  benefitBreakdowns: SegmentPremiumBreakdownDTO;
  cirSpec: any;
  puaOption: string;
};
export interface AlirSpecificationsPayload {
  frequency: PaymentFrequency;
  nextPaymentDay: Date;
  nextPaymentAmount: number;
  annualMax: number;
  paymentAmountAvailable: number;
  paymentsYTD: number;
  endDate: Date;
}

export declare type AlirSectionDTO = {
  alirSpec: AlirSpecificationsPayload;
  activeSegment: SegmentDTO;
  benefitBreakdowns: SegmentPremiumBreakdownDTO;
  underwritingClass: string;
  coverageAmount: number;
};

export declare type CombinedSegmentDTO = {
  payload: {
    activeSegments: Array<SegmentDTO>;
    segmentPremiums: SegmentPremiumDTO;
    benefitPremiums: Array<BenefitPremiumDTO>;
    GIR?: GirSectionDTO;
    CIR?: CirSectionDTO;
    RTR?: RtrSectionDTO;
    ALIR?: AlirSectionDTO;
  };
};
export interface PolicyHeaderDTO {
  payload: {
    policyNumber: string;
    contractState: string;
    notes: Array<PolicyNoteDTO>;
    policyHoldStatus: string;
    replacementStatus: string;
    nonForfeitureOption: string;
    detailsOf1035Exchange: DetailsOf1035ExchangeDTO | undefined;
  };
}

export type DetailsOf1035ExchangeDTO = {
  purposeOf1035Exchange: string;
  cashValueTransfer: number | null;
  finalCashValueTransfer: number | null;
  costBasis: number | null;
  finalCostBasis: number | null;
  issueDate1035: Date | null;
};

export interface BillingInfoDTO {
  invoices: Array<InvoiceDTO>;
  accumulatedDividends: number;
}

export interface PolicyValuesDTO {
  apl: boolean;
  loanInterestType: string;
  totalLoanBalance: number;
  uncapitalizedInterestAmount: number;
}
export interface BasicPolicyInfoDTO {
  payload: {
    policyNumber: string;
    contractState: string;
    notes: Array<PolicyNoteDTO>;
    policyHoldStatus: string;
    replacementStatus: string;
    nonForfeitureOption: string;
    detailsOf1035Exchange: DetailsOf1035ExchangeDTO | undefined;
  };
}
@supertypeClass({ toClient: true, toServer: false })
export class PolicyAdminRiders extends Remoteable(Supertype) {
  @property()
  private GIR: GirSectionDTO;

  @property()
  private CIR: CirSectionDTO;

  @property()
  private RTR: RtrSectionDTO;

  @property()
  private ALIR: AlirSectionDTO;

  @property()
  private flatExtras: FlatExtraDTO;

  @property()
  waiverOfPremium: boolean = false;

  @property()
  adbr: boolean = false;

  @property()
  transferOfInsured: boolean = false;

  @property()
  automaticPremiumLoan: boolean = false;

  @property()
  dividendOption: string;

  @property()
  segmentInfo: CombinedSegmentDTO;

  @property()
  productInfo: ProductInfoPayload;

  @property()
  private exchange1035: DetailsOf1035ExchangeDTO;

  @property()
  policyValues: PolicyValuesDTO;

  @property()
  basicPolicyInfo: BasicPolicyInfoDTO;

  @property()
  nonForfeitureOption: string;

  constructor() {
    super();
  }

  static new(policyAdminSegmentInfo: CombinedSegmentDTO) {
    return new PolicyAdminRiders().init(policyAdminSegmentInfo);
  }

  init(policyAdminSegmentInfo: CombinedSegmentDTO) {
    if (policyAdminSegmentInfo && policyAdminSegmentInfo.payload) {
      this.segmentInfo = policyAdminSegmentInfo;

      if (policyAdminSegmentInfo.payload.GIR) {
        this.GIR = policyAdminSegmentInfo.payload.GIR;
      }
      if (policyAdminSegmentInfo.payload.CIR) {
        this.CIR = policyAdminSegmentInfo.payload.CIR;
      }
      if (policyAdminSegmentInfo.payload.RTR) {
        this.RTR = policyAdminSegmentInfo.payload.RTR;
      }
      if (policyAdminSegmentInfo.payload.ALIR) {
        this.ALIR = policyAdminSegmentInfo.payload.ALIR;
      }
    }
    const policySegment = policyAdminSegmentInfo.payload.activeSegments.find(
      (segment) => segment.segmentType === 'PolicySegment'
    );
    policySegment.segmentBenefits.forEach((benefit) => {
      switch (benefit.policyBenefit.benefitKey.benefitCode) {
        case BenefitCode.Adbr:
          this.adbr = true;
          break;
        case BenefitCode.Waiver:
          this.waiverOfPremium = true;
          break;
        case BenefitCode.TIR:
          this.transferOfInsured = true;
          break;
      }
    });

    return this;
  }

  public getGIRRiderInfo() {
    if (this.GIR) {
      return {
        currentExercisedAmount: this.GIR.coverageAmount,
      };
    }
    return null;
  }

  public getCIRRiderInfo() {
    if (this.CIR) {
      return {
        benefitPoolAmount: this.CIR.coverageAmount,
        payoutOption: this.CIR.monthlyAccelerationPercentage,
        paidUpAdditionsOption: this.CIR.puaOption,
      };
    }
    return null;
  }

  public getRTRRiderInfo() {
    if (this.RTR) {
      return {
        maxAmount: this.RTR.coverageAmount,
      };
    }
    return null;
  }

  public getALIRRiderInfo() {
    if (this.ALIR) {
      const annualMaximum = this.ALIR.alirSpec.annualMax;
      let paymentSchedule;
      if (this.ALIR.alirSpec.nextPaymentAmount && this.ALIR.alirSpec.frequency) {
        const formattedFrequency = Utils.policyPaymentFrequencyToText(this.ALIR.alirSpec.frequency.toLowerCase());
        const formattedAmount = Utils.formatCurrency(this.ALIR.alirSpec.nextPaymentAmount, 2);
        paymentSchedule = `${formattedAmount}${formattedFrequency}`;
      } else {
        paymentSchedule = null;
      }
      return {
        paymentSchedule,
        annualMaximum,
        endDate: this.ALIR.alirSpec.endDate,
        premium: this.ALIR.alirSpec.nextPaymentAmount,
        paymentFrequency: this.ALIR.alirSpec.frequency,
        amountPaid: this.ALIR.alirSpec.paymentsYTD,
        paymentAmountAvailable: this.ALIR.alirSpec.paymentAmountAvailable,
      };
    }
    return null;
  }

  public get1035ExchangeRiderInfo() {
    if (this.exchange1035) {
      return {
        status: this.exchange1035.issueDate1035,
        amount: this.exchange1035.finalCashValueTransfer
          ? this.exchange1035.finalCashValueTransfer
          : this.exchange1035.cashValueTransfer,
        costBasis: this.exchange1035.finalCostBasis ? this.exchange1035.finalCostBasis : this.exchange1035.costBasis,
        applyTowards: this.exchange1035.purposeOf1035Exchange,
      };
    }
    return null;
  }

  public setProductInfo(productInfo: ProductInfoPayload) {
    this.productInfo = productInfo;
    this.dividendOption = productInfo.dividendOption;
  }

  public setPolicyValues(policyValues: PolicyValuesDTO) {
    this.policyValues = policyValues;
    this.automaticPremiumLoan = policyValues.apl;
  }

  public setBasicPolicyInfo(basicPolicyInfoResponse: BasicPolicyInfoDTO) {
    this.basicPolicyInfo = basicPolicyInfoResponse;
    this.exchange1035 = basicPolicyInfoResponse.payload.detailsOf1035Exchange;
    this.nonForfeitureOption = basicPolicyInfoResponse.payload.nonForfeitureOption;
  }

  public getCACPostRiderInfo(): CACPostRiderDTO {
    return {
      adbr: this.adbr,
      waiverOfPremium: this.waiverOfPremium,
    };
  }
}
